import {
  AnnotationType,
  Piece,
  PieceAnnotation,
  TrackingNumberAnnouncementStatus,
  TrackingNumberStatus,
  TrackingNumberValidationStatus,
  Workflow,
} from '@apiContract';
import { IconColors } from '@components/Capture/types';
// eslint-disable-next-line import/named
import { filter, isArray } from 'lodash/fp';

type ValidationMap = Map<TrackingNumberValidationStatus, number>;
type AnnounceMap = Map<TrackingNumberAnnouncementStatus, number>;

export type BarcodeListValidationSummary = {
  validation: ValidationMap;
  announce: AnnounceMap;
};

const updateStatusValueMap = (
  map: Map<unknown, number>,
  key: TrackingNumberValidationStatus | TrackingNumberAnnouncementStatus,
) => {
  const keyValue = map.get(key);
  if (keyValue) {
    map.set(key, keyValue + 1);
  } else {
    map.set(key, 1);
  }
};

export const getTrackingNumberAnnotations = (annotations: Array<PieceAnnotation> | null) =>
  filter(
    ({ textValue, annotationType, pieceAnnotationsTrackingNumber }: PieceAnnotation) =>
      !!textValue && !!pieceAnnotationsTrackingNumber && annotationType === AnnotationType.TrackingNumber,
    annotations,
  );

export const getBarcodeIconColor = (piece?: Piece | null): IconColors => {
  if (piece?.pieceSummary?.trackingNumberStatus === TrackingNumberStatus.Valid) {
    return 'success';
  }

  if (piece?.pieceSummary?.trackingNumberStatus === TrackingNumberStatus.Missing) {
    return 'warning';
  }

  return 'error';
};

export const isTrackingNumberValid = (annotation: PieceAnnotation, workflow?: Workflow) => {
  const { announcementStatus, validationStatus } = annotation.pieceAnnotationsTrackingNumber || {};
  if (workflow === Workflow.Lockers) {
    return (
      validationStatus === TrackingNumberValidationStatus.Valid &&
      announcementStatus === TrackingNumberAnnouncementStatus.Confirmed
    );
  }

  return validationStatus === TrackingNumberValidationStatus.Valid;
};

export const createAnnotationMapObject = (annotations: PieceAnnotation[]) =>
  annotations.reduce<{ [key: number]: PieceAnnotation | PieceAnnotation[] }>((acc, item) => {
    if (!acc[item.annotationType]) {
      acc[item.annotationType] = item;
    } else if (!isArray(acc[item.annotationType])) {
      acc[item.annotationType] = [acc[item.annotationType] as PieceAnnotation, item];
    } else {
      (acc[item.annotationType] as PieceAnnotation[]).push(item as PieceAnnotation);
    }
    return acc;
  }, {});

export type BarcodesStatusesCounts = {
  [TrackingNumberValidationStatus.Valid]: number;
  [TrackingNumberValidationStatus.BadFormat]: number;
  [TrackingNumberValidationStatus.Conflict]: number;
  all: number;
}

export const getBarcodesStatusesCounts = (annotations: PieceAnnotation[]): BarcodesStatusesCounts =>
  annotations.reduce(
    (acc, item) => {
      if (item.pieceAnnotationsTrackingNumber) {
        acc.all += 1;
        acc[item.pieceAnnotationsTrackingNumber.validationStatus] += 1;
      }
      return acc;
    },
    {
      [TrackingNumberValidationStatus.Valid]: 0,
      [TrackingNumberValidationStatus.BadFormat]: 0,
      [TrackingNumberValidationStatus.Conflict]: 0,
      all: 0,
    },
  );
